import * as React from "react"

import { cn } from "@/lib/utils"
import { THEME, THEME_ORDER, type Theme } from "@/components/login-theme"
// 橙色四色轮盘：走打包导入(带 hash)而非 public 绝对路径,避免部署环境取不到静态资源时破图
import themeWheel from "@/assets/images/theme-wheel.svg"

// 视觉对齐登录页 ThemeSwitcher（src/pages/admin/login.tsx）：
// 白底胶囊，当前主题在最左（26px，橙色用 theme-wheel.svg 四色轮盘，其他用纯色大圆点），
// 1×29 浅灰竖线分隔，其余 3 个主题以 24px 纯色圆点排列在右，可点击切换。
const STORAGE_KEY = "eeelt_theme"
const DEFAULT_THEME: Theme = "orange"

type ThemeSelectorProps = {
  className?: string
  compact?: boolean
  /** 兼容旧接口：是否在按钮上方显示「主题」字样（新设计无字样，保留旧 prop 仅用于不破坏调用方） */
  showLabel?: boolean
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") {
    return DEFAULT_THEME
  }
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return THEME_ORDER.includes(stored as Theme) ? (stored as Theme) : DEFAULT_THEME
}

function Swatch({ theme, size }: { theme: Theme; size: number }) {
  if (theme === "orange") {
    return (
      <img
        src={themeWheel}
        alt=""
        style={{ width: size, height: size, display: "block" }}
      />
    )
  }
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: THEME[theme].dot,
        display: "block",
      }}
    />
  )
}

export default function ThemeSelector({
  className,
  compact = false,
  showLabel: _showLabel = false,
}: ThemeSelectorProps) {
  const [theme, setTheme] = React.useState<Theme>(getInitialTheme)

  React.useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme)
    window.localStorage.setItem(STORAGE_KEY, theme)
  }, [theme])

  const others = THEME_ORDER.filter((t) => t !== theme)

  // 尺寸按 compact 缩放（compact 下整体缩约 80%），形态/分隔线/顺序均与登录页一致。
  const sizeCurrent = compact ? 22 : 26
  const sizeOther = compact ? 20 : 24
  const dividerH = compact ? 24 : 29
  const gap = compact ? 10 : 13
  const padX = compact ? 10 : 12
  const height = compact ? 32 : 39

  return (
    <div
      className={cn("inline-flex items-center bg-white shadow-sm", className)}
      style={{
        height,
        gap,
        padding: `0 ${padX}px`,
        borderRadius: 28.5,
      }}
    >
      <button
        type="button"
        aria-label={`current theme ${theme}`}
        aria-pressed
        style={{
          border: 0,
          background: "transparent",
          cursor: "default",
          padding: 0,
          lineHeight: 0,
        }}
      >
        <Swatch theme={theme} size={sizeCurrent} />
      </button>
      <span style={{ width: 1, height: dividerH, background: "#F3F3F4" }} />
      {others.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => setTheme(t)}
          aria-label={`switch to ${t} theme`}
          className="transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
          style={{
            border: 0,
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            lineHeight: 0,
          }}
        >
          <Swatch theme={t} size={sizeOther} />
        </button>
      ))}
    </div>
  )
}
